import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { PageHeader, StateBlock } from '../../shared/StateBlock'
import { ResponsiveTable } from '../../shared/ResponsiveTable'
import { ibApi, IbApiError, type IbSubadmin } from '../api'
import { useIbAuth } from '../AuthProvider'

export function IbWalletPage() {
  const { logout } = useIbAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [subadmin, setSubadmin] = useState<IbSubadmin | null>(null)
  const [ledger, setLedger] = useState<Array<Record<string, unknown>>>([])
  const [requests, setRequests] = useState<Array<Record<string, unknown>>>([])
  const [type, setType] = useState<'DEPOSIT' | 'WITHDRAWAL'>('DEPOSIT')
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pwBusy, setPwBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await ibApi.wallet()
      setSubadmin(data.subadmin)
      setLedger(data.ledger || [])
      setRequests(data.requests || [])
    } catch (err) {
      setError(err instanceof IbApiError ? err.message : 'Unable to load wallet')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function onRequest(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    setNotice('')
    try {
      const value = Number(amount)
      if (!Number.isFinite(value) || value <= 0) {
        throw new IbApiError('Enter a valid amount')
      }
      await ibApi.walletRequest(type, value, note.trim())
      setNotice(
        `${type === 'DEPOSIT' ? 'Deposit' : 'Withdrawal'} request submitted for Admin review.`,
      )
      setAmount('')
      setNote('')
      await load()
    } catch (err) {
      setError(err instanceof IbApiError ? err.message : 'Unable to submit request')
    } finally {
      setBusy(false)
    }
  }

  async function onChangePassword(e: FormEvent) {
    e.preventDefault()
    setPwBusy(true)
    setError('')
    setNotice('')
    try {
      if (newPassword.length < 8) throw new IbApiError('New password must be at least 8 characters')
      if (newPassword !== confirmPassword) {
        throw new IbApiError('New password confirmation does not match')
      }
      await ibApi.changePassword(currentPassword, newPassword)
      setNotice('Password updated. Sign in again with the new password.')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      window.setTimeout(() => {
        logout()
        navigate('/ib/login', { replace: true })
      }, 900)
    } catch (err) {
      setError(err instanceof IbApiError ? err.message : 'Unable to change password')
    } finally {
      setPwBusy(false)
    }
  }

  return (
    <div>
      <PageHeader eyebrow="IB portal" title="Wallet">
        <p className="cp-muted">
          Deposit commissions unlock after the hold period. Wallet withdrawals need Main Admin
          approval and cannot use still-locked commission or go below minimum reserve.
        </p>
      </PageHeader>

      {error ? (
        <StateBlock tone="error" title="Error">
          {error}
        </StateBlock>
      ) : null}
      {notice ? (
        <StateBlock tone="info" title="Submitted">
          {notice}
        </StateBlock>
      ) : null}
      {loading ? (
        <p className="cp-muted ap-inline-status" aria-live="polite">
          Loading wallet…
        </p>
      ) : null}

      <div className={loading ? 'is-refreshing' : undefined}>
        <div className="cp-kpi-grid">
          <article className="cp-kpi">
            <span>Total commission</span>
            <strong>
              {loading
                ? '—'
                : `$${Number(subadmin?.totalCommission || 0).toFixed(2)}`}
            </strong>
          </article>
          <article className="cp-kpi">
            <span>Withdrawable commission</span>
            <strong>
              {loading
                ? '—'
                : `$${Number(subadmin?.withdrawableCommission || 0).toFixed(2)}`}
            </strong>
            <em>
              {loading
                ? 'Hold —'
                : `${subadmin?.commissionHoldDays ?? 14}-day hold · pending $${Number(subadmin?.pendingCommission || 0).toFixed(2)}`}
            </em>
          </article>
          <article className="cp-kpi">
            <span>Wallet balance</span>
            <strong>
              {loading ? '—' : `$${Number(subadmin?.balance || 0).toFixed(2)}`}
            </strong>
          </article>
          <article className="cp-kpi">
            <span>Wallet withdrawable</span>
            <strong>
              {loading ? '—' : `$${Number(subadmin?.withdrawable || 0).toFixed(2)}`}
            </strong>
            <em>
              {loading
                ? 'Reserve —'
                : `After reserve $${Number(subadmin?.minimumReserve || 0).toFixed(2)} + hold lock`}
            </em>
          </article>
        </div>

        <section className="cp-panel" style={{ marginTop: '1.25rem' }}>
          <h2>Change password</h2>
          <form className="cp-auth__form" onSubmit={(e) => void onChangePassword(e)}>
            <label className="cp-field">
              <span className="cp-field__label">Current password</span>
              <input
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </label>
            <label className="cp-field">
              <span className="cp-field__label">New password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={8}
                required
              />
            </label>
            <label className="cp-field">
              <span className="cp-field__label">Confirm new password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                minLength={8}
                required
              />
            </label>
            <Button type="submit" disabled={pwBusy}>
              {pwBusy ? 'Updating…' : 'Update password'}
            </Button>
          </form>
        </section>

        <section className="cp-panel" style={{ marginTop: '1.25rem' }}>
          <h2>Request deposit / withdraw</h2>
          <form className="cp-auth__form" onSubmit={(e) => void onRequest(e)}>
            <label className="cp-field">
              <span className="cp-field__label">Type</span>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as 'DEPOSIT' | 'WITHDRAWAL')}
              >
                <option value="DEPOSIT">Deposit</option>
                <option value="WITHDRAWAL">Withdrawal</option>
              </select>
            </label>
            <label className="cp-field">
              <span className="cp-field__label">Amount (USD)</span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </label>
            <label className="cp-field">
              <span className="cp-field__label">Note</span>
              <input value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
            <Button type="submit" disabled={busy}>
              {busy ? 'Submitting…' : 'Submit request'}
            </Button>
          </form>
        </section>

        <section style={{ marginTop: '1.5rem' }}>
          <h2>Requests</h2>
          <ResponsiveTable
            rowKey={(r, i) => String(r.id ?? i)}
            columns={[
              { key: 'id', header: 'ID', render: (r) => String(r.id) },
              {
                key: 'type',
                header: 'Type',
                render: (r) => String(r.request_type || r.type || '—'),
              },
              {
                key: 'amount',
                header: 'Amount',
                render: (r) => `$${Number(r.amount || 0).toFixed(2)}`,
              },
              { key: 'status', header: 'Status', render: (r) => String(r.status || '—') },
              {
                key: 'created',
                header: 'Created',
                render: (r) =>
                  String(r.created_at || r.createdAt || '').slice(0, 16) || '—',
              },
            ]}
            rows={requests}
            empty={loading ? 'Loading…' : 'No wallet requests yet.'}
          />
        </section>

        <section style={{ marginTop: '1.5rem' }}>
          <h2>Ledger</h2>
          <ResponsiveTable
            rowKey={(r, i) => String(r.id ?? i)}
            columns={[
              {
                key: 'type',
                header: 'Type',
                render: (r) => String(r.entry_type || r.type || '—'),
              },
              {
                key: 'amount',
                header: 'Amount',
                render: (r) => `$${Number(r.amount || 0).toFixed(2)}`,
              },
              {
                key: 'balance',
                header: 'Balance after',
                render: (r) =>
                  `$${Number(r.balance_after || r.balanceAfter || 0).toFixed(2)}`,
              },
              { key: 'note', header: 'Note', render: (r) => String(r.note || '—') },
              {
                key: 'created',
                header: 'When',
                render: (r) =>
                  String(r.created_at || r.createdAt || '').slice(0, 16) || '—',
              },
            ]}
            rows={ledger}
            empty={loading ? 'Loading…' : 'No ledger entries yet.'}
          />
        </section>
      </div>
    </div>
  )
}
